import { useMemo } from 'react';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { IconButton } from '@mui/material';

import { navigate, routes } from '@redwoodjs/router';
import { useQuery } from '@redwoodjs/web';

import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import { NPIDataGrid, NPIDataGridColDef } from 'src/components/NPIDataGrid/NPIDataGrid';
import { createColumnDef } from 'src/components/NPIDataGrid/NPIDataGrid.utils';
import { ACCOUNT_STATEMENTS } from 'src/graphql/queries/accountStatements.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import formatCentsToLocaleCurrency from 'src/utils/finance';
import { formatDate, onError } from 'src/utils/utils';

type Statement = {
    id: number;
    accountStatementId: string;
    merchantName: string;
    merchantAccountNumber: string;
    statementDate: string;
    outstandingBalance: number;
};

function AccountStatementsPage() {
    const maxHeight = '400';

    const { data, loading } = useQuery(ACCOUNT_STATEMENTS, {
        fetchPolicy: 'cache-and-network',
        onError,
    });

    const tableData = useMemo(() => {
        return (
            data?.getAccountStatements.map((item) => {
                return {
                    ...item,
                    merchantName: item.merchant.tradingName,
                    merchantAccountNumber: item.merchant.accountNumber,
                    outstandingBalance: formatCentsToLocaleCurrency(
                        item.outstandingBalance,
                        navigator.language
                    ),
                };
            }) ?? []
        );
    }, [data]);

    const openView = (statement: Statement) => {
        navigate(routes.viewAccountStatement({ id: statement.id }));
    };

    const STATEMENTS_TABLE_HEADERS: NPIDataGridColDef[] = [
        createColumnDef({
            field: 'accountStatementId',
            headerName: 'Statement ID',
        }),
        createColumnDef({
            field: 'merchantName',
            headerName: 'Merchant Name',
        }),
        createColumnDef({
            field: 'merchantAccountNumber',
            headerName: 'Merchant Account Number',
        }),
        createColumnDef({
            field: 'statementDate',
            headerName: 'Statement Date',
            renderCell: (params) => {
                const date = new Date(params?.row?.statementDate);
                return formatDate({ date });
            },
        }),
        createColumnDef({
            field: 'outstandingBalance',
            headerName: 'Outstanding Balance',
        }),
        createColumnDef({
            field: 'view',
            flex: 0,
            minWidth: 160,
            renderCell: (params) => {
                return (
                    <Flex gap="8px">
                        <IconButton
                            aria-label="view"
                            data-testid="view"
                            onClick={() => {
                                openView(params.row);
                            }}
                        >
                            <VisibilityOutlinedIcon />
                        </IconButton>
                    </Flex>
                );
            },
        }),
    ];

    return (
        <PageContent
            heading="Account Statements"
            subHeading="Full account statements showing Invoice, Payments and Credit Notes"
        >
            {loading && <Loader />}
            <NPIDataGrid
                dataTestId="account-statements-table"
                rows={tableData}
                columns={STATEMENTS_TABLE_HEADERS}
                getRowId={(row) => {
                    return row.id;
                }}
                title="Account Statements"
                maxHeight={maxHeight}
            />
        </PageContent>
    );
}

export default AccountStatementsPage;
